import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import BackIcon from '@mui/icons-material/ArrowBack';
import { NavLink } from 'react-router-dom';
import { UserContext } from "../../UserProvider"
import { httpGet } from '../../Web';
import { Autocomplete, Breadcrumbs, Box, Button, Stack, TextField, Typography, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { BreadCrumbProps } from "../../subs/PageAlternate";
import { isGlobalAdmin, UserKeyValidationResult } from '../../types/User';
import { CompaniesAdminView } from '../Administration/DBViews/CompaniesAdminView';
import { UnsupportedTestCasesAdminView } from '../Administration/DBViews/UnsupportedTestCasesAdminView';
import { UsersAdminView } from '../Administration/DBViews/UsersAdminView';
import { WorkshopProductCombinationAdminView } from '../Administration/DBViews/WorkshopProductCombinationAdminView';
import { WorkshopProductPropertiesAdminView } from '../Administration/DBViews/WorkshopProductPropertiesAdminView';
import { WorkshopProductsAdminView } from '../Administration/DBViews/WorkshopProductsAdminView';
import { WorkshopUsersAdminView } from '../Administration/DBViews/WorkshopUsersAdminView';
import { WorkshopAdminView } from '../Administration/DBViews/WorkshopAdminView';
import { WorkshopInterfacesAdminView } from '../Administration/DBViews/WorkshopInterfacesAdminView';
import { WorkshopProductInterfacesAdminView } from '../Administration/DBViews/WorkshopProductInterfacesAdminView';
import { WorkshopResultsAdminView } from '../Administration/DBViews/WorkshopResultsAdminView';
import { WorkshopTestCaseResultsAdminView } from '../Administration/DBViews/WorkshopTestCaseResultsAdminView';
import { WorkshopTestCasesAdminView } from '../Administration/DBViews/WorkshopTestCasesAdminView';
import { ProductsAdminView } from '../Administration/DBViews/ProductsAdminView';
import { TestComboSupportAdminView } from '../Administration/DBViews/TestComboSupportAdminView';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../contexts/SnackBarContext';

// global theming ruins the back button, disable it here
const backTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                },
            },
        }
    }
});
const breadcrumbs: BreadCrumbProps[] = [
    { to: "/Administration", text: "Administration" },
    { to: "/DBFunctionPage", text: "Functions" },
];

/*
no priority
    ["InterfaceProperties", "Interfaces", "Testcases", "TestCombinations"];     // have alternative views or not worth the effort for now
Done
    ["Companies", "Products", "TestComboSupport", "UnsupportedTestcases", "Users", 
    "Workshops", "WorkshopInterfaces", "WorkshopProducts", "WorkshopProductCombinations", "WorkshopProductProperties", "WorkshopUsers"];
    "WorkshopProductInterfaces", "WorkshopResults", "WorkshopTestCaseResults", "WorkshopTestCases"];
Keys:  
        Companies:                  pk - id
        DBVersioninfo:              pk - id
        InterfaceProperties:        pk - id
                                    uk - interfaceId + Group + Name
                                    fk - interfaceId -> Interfaces
        Interfaces:                 pk - id
                                    uk - Name                                   / ShortName
        Products:                   pk - id
                                    fk - companyId -> Companies
        Testcases                   pk - id
                                    uk - InterfaceId + Group + Name
                                    fk - interfaceId -> Interfaces
        TestCombinations            pk - id
        TestComboSupport            pk - id
        UnsupportedTestCases        pk - id
                                    uk - workshopProductId + testCaseId
                                    fk - workshopProductId -> WorkshopProducts  / testCaseId -> Testcases
        Users                       pk - id
                    uk - NONE - needed currently have dups
                                    fk - companyId -> Companies
        WorkshopInterfaces          pk - id
                                    uk - workshopId + interfaceId
                                    fk - workshopId -> Workshops                / interfaceId -> Interfaces
        WorkshopProductCombinations pk - id
        WorkshopProductInterfaces   pk - id
                                    uk - workshopProductId + interfaceId
                                    fk - workshopProductId -> WorkshopProducts  / interfaceId -> Interfaces
        WorkshopProductProperties   pk - id
                                    uk - workshopProductId + InterfacePropertyId + TestCombinationId
                                    fk - workshopProductId -> WorkshopProducts  / InterfacePropertyId -> InterfaceProperties
        WorkshopProducts            pk - id
                                    fk - workshopId -> Workshops                / productId -> Products
        WorkshopResults             pk - id
                                    uk - InterfaceId + ServerId + ClientId + testCombinationId
                                    fk - InterfaceId -> Interfaces              / ServerId -> WorkshopProducts      / ClientId -> WorkshopProducts
        WorkshopTestCaseResults     pk - id
                                    uk - WorkshopResultId + TestCaseId
                                    fk - WorkshopResultId -> WorkshopResults    / TestCaseId -> Testcases
        WorkshopTestCases           pk - id
        WorkshopUsers               pk - id
                                    uk - workshopId + userId
                                    fk - workshopId -> Workshops                / userId -> Users
        Workshops                   pk - id
                                    uk - Name
                                    fk - CreatorId -> Users
*/

const tableOptions = ["No Selection", "Companies", "Products", "TestComboSupport", "UnsupportedTestcases", "Users",
    "Workshops", "WorkshopInterfaces", "WorkshopProducts", "WorkshopProductCombination", "WorkshopProductProperties", "WorkshopUsers",
    "WorkshopProductInterfaces", "WorkshopResults", "WorkshopTestCaseResults", "WorkshopTestCases"];

export const DBViewDevDebug = () => {
    const userContext = React.useContext(UserContext);
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const [isGlAdmin, setIsGlAdmin] = React.useState<boolean>(false);
    const [isAuthourized, setIsAuthourized] = React.useState<boolean>(false);
    const [selectedTable, setSelectedTable] = React.useState<number>(0);
    const [modeSelectionKey, setModeSelectionKey] = React.useState<string>("");

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    const backButtonHandler = () => navigate(-1);

    React.useEffect(() => {
        const fetch = async () => {
            if (modeSelectionKey === "") {
                return;
            }
            const validKeyResponse = await httpGet(`/api/user/checkkey/${modeSelectionKey}`, new AbortController());
            
            if (validKeyResponse.failed) {
                showUserFeedback(validKeyResponse.errorText, "error");
                return;
            }
            const userKeyValidationResult: UserKeyValidationResult = validKeyResponse.result;
            setIsAuthourized(userKeyValidationResult.keyValid);
        }
        fetch();
    }, [modeSelectionKey, showUserFeedback]);

    React.useEffect(() => {
        const fetch = async () => {
            if (userContext.user.id === 0) {
                showUserFeedback("You are not logged in, please log in to access this page.", "error");
                return;
            }
            const isAdmin = await isGlobalAdmin(userContext.user.id!!, new AbortController());
            if (!isAdmin) {
                showUserFeedback("You are not allowed to access this page.", "error");
                return;
            }
            setIsGlAdmin(isAdmin);
        }
        fetch();
    }, [userContext.user.id, showUserFeedback]);

    return <>
        <Stack direction="row" spacing={200}>
            <ThemeProvider theme={backTheme}>
                <Button startIcon={<BackIcon />} size="small" onClick={backButtonHandler}>back</Button>
            </ThemeProvider>
            {breadcrumbs && <Breadcrumbs separator="|">
                {breadcrumbs.map((crumb, index) => (
                    <NavLink key={index} to={crumb.to}>{crumb.text}</NavLink>
                ))}
            </Breadcrumbs>}
        </Stack>
        <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
            {   !isGlAdmin ?
                    <Typography variant='h4' sx={{ textAlign: 'center', marginTop: '20px' }}>You are not autorized to view this page...</Typography> :
                !isAuthourized ?
                    <Grid container spacing={2} alignItems={'center'} sx={{ marginTop: '20px' }}>
                        <Grid size={12} paddingBottom={10}>
                            <Typography variant='h4' sx={{ textAlign: 'center', marginTop: '20px' }}>Please Enter the Developers secret.</Typography>
                        </Grid>
                        <Grid size={12} paddingBottom={10}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel shrink htmlFor='Key'>{"Key"}</InputLabel>
                                <OutlinedInput notched name='Key' id='Key'
                                    label={"Key"} value={modeSelectionKey}
                                    onChange={(e) => setModeSelectionKey(e.target.value)}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    :
                    <Grid container spacing={2} alignItems={'center'} sx={{ marginTop: '20px' }}>
                        <Grid size={12} paddingBottom={10}>
                            <Typography variant='h4' sx={{ textAlign: 'center', marginTop: '20px' }}>Please Select the data you wish to view.</Typography>
                        </Grid>
                        <Grid size={6} paddingBottom={10}>
                            <FormControl required fullWidth>
                                <Autocomplete id='ac-tableOptions' options={tableOptions}
                                    value={tableOptions[selectedTable]}
                                    onChange={(e, value) => setSelectedTable(tableOptions.indexOf(value as string))}
                                    renderInput={(params) =>
                                        <TextField name='tableOptions' multiline
                                            {...params}
                                            label={"Table"}
                                            sx={{
                                                '& .MuiOutlinedInput-root fieldset': {
                                                    borderColor: 'divider',
                                                },
                                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                                    borderColor: 'divider',
                                                },
                                            }}
                                        />
                                    }
                                />
                            </FormControl>
                        </Grid>
                        {
                            tableOptions[selectedTable] === "Companies" ? <CompaniesAdminView /> :
                            tableOptions[selectedTable] === "Products" ? <ProductsAdminView /> :
                            tableOptions[selectedTable] === "UnsupportedTestcases" ? <UnsupportedTestCasesAdminView /> :
                            tableOptions[selectedTable] === "Users" ? <UsersAdminView /> :
                            tableOptions[selectedTable] === "Workshops" ? <WorkshopAdminView /> :
                            tableOptions[selectedTable] === "WorkshopUsers" ? <WorkshopUsersAdminView /> :
                            tableOptions[selectedTable] === "WorkshopInterfaces" ? <WorkshopInterfacesAdminView /> :
                            tableOptions[selectedTable] === "WorkshopProducts" ? <WorkshopProductsAdminView /> :
                            tableOptions[selectedTable] === "WorkshopProductInterfaces" ? <WorkshopProductInterfacesAdminView /> :
                            tableOptions[selectedTable] === "WorkshopProductCombination" ? <WorkshopProductCombinationAdminView /> :
                            tableOptions[selectedTable] === "WorkshopProductProperties" ? <WorkshopProductPropertiesAdminView /> :
                            tableOptions[selectedTable] === "WorkshopResults" ? <WorkshopResultsAdminView /> :
                            tableOptions[selectedTable] === "WorkshopTestCaseResults" ? <WorkshopTestCaseResultsAdminView /> :
                            tableOptions[selectedTable] === "WorkshopTestCases" ? <WorkshopTestCasesAdminView /> :
                            tableOptions[selectedTable] === "TestComboSupport" ? <TestComboSupportAdminView /> :
                            null
                        }
                    </Grid>
            }
        </Box>
    </>
}
